import {
  ascendRealm, buyActiveBuildingUpgrade, buyActiveToolUpgrade, buyConstellationNode, buyPrestigeUpgrade,
  createInitialState, GameLoop, performManualAction, selectActiveSkill, sellResource,
  setPassiveSkills, claimContractReward, chooseDoctrine, openRealmRift, useReward,
  craftArtifact, recordContractProgress,
  type ActivityActionResult, type CriticalTrainingEvent, type DoctrineActionResult, type DoctrineId, type EconomyActionResult, type GameState, type ManualActionResult,
  type ConstellationNodeId, type PrestigeActionResult, type PrestigeUpgradeId, type SimulationResult, type SkillId
} from '@rsvjelle/game-core';
import { AutosaveController, IndexedDbSaveDriver, SaveRepository } from '@rsvjelle/storage';

export interface BootResult {
  session: BrowserGameSession;
  offline: SimulationResult | null;
  recoveredSave: boolean;
}

export class BrowserGameSession {
  private readonly loop: GameLoop;
  private readonly autosave: AutosaveController;
  private readonly listeners = new Set<(state: GameState) => void>();
  private unsubscribeLoop: (() => void) | null = null;
  private readonly visibilityHandler = () => {
    if (document.visibilityState === 'hidden') {
      this.loop.stop();
      void this.autosave.flush();
      return;
    }
    const offline = this.loop.restoreOffline(Date.now());
    this.loop.start();
    if (offline.simulatedSeconds > 0) window.dispatchEvent(new CustomEvent('rsv2:offline-progress', { detail: offline }));
  };

  private constructor(state: GameState, repository: SaveRepository) {
    this.loop = new GameLoop(state);
    this.autosave = new AutosaveController(repository, () => this.loop.snapshot());
    this.unsubscribeLoop = this.loop.subscribe((nextState, result) => {
      this.emit(nextState);
      if (result.criticalEvents?.length) {
        window.dispatchEvent(new CustomEvent<CriticalTrainingEvent[]>('rsv2:critical-training', { detail: result.criticalEvents }));
      }
    });
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  static async boot(): Promise<BootResult> {
    const repository = new SaveRepository(new IndexedDbSaveDriver());
    const loaded = await repository.load();
    const initial = recordContractProgress(loaded?.state ?? createInitialState(crypto.randomUUID()));
    const session = new BrowserGameSession(initial, repository);
    const offline = loaded ? session.loop.restoreOffline(Date.now()) : null;
    await repository.save(session.loop.snapshot());
    if (document.visibilityState !== 'hidden') session.loop.start();
    session.autosave.start();
    return { session, offline, recoveredSave: loaded?.recovered ?? false };
  }

  snapshot(): GameState { return this.loop.snapshot(); }

  subscribe(listener: (state: GameState) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  selectSkill(skill: SkillId): void {
    this.loop.tick(Date.now());
    const next = selectActiveSkill(this.loop.snapshot(), skill);
    this.loop.replaceState(next);
    this.emit(next);
  }

  manualAction(): ManualActionResult {
    const result = performManualAction(this.loop.snapshot(), Date.now());
    this.loop.replaceState(result.state);
    this.emit(result.state);
    return result;
  }

  sell(resource: string, quantity: number): EconomyActionResult {
    this.loop.tick(Date.now());
    return this.applyEconomyAction(sellResource(this.loop.snapshot(), resource, quantity));
  }

  buyTool(): EconomyActionResult {
    this.loop.tick(Date.now());
    return this.applyEconomyAction(buyActiveToolUpgrade(this.loop.snapshot()));
  }

  buyBuilding(): EconomyActionResult {
    this.loop.tick(Date.now());
    return this.applyEconomyAction(buyActiveBuildingUpgrade(this.loop.snapshot()));
  }

  ascend(): PrestigeActionResult {
    this.loop.tick(Date.now());
    return this.applyPrestigeAction(ascendRealm(this.loop.snapshot()));
  }

  buyPrestigeUpgrade(upgrade: PrestigeUpgradeId): PrestigeActionResult {
    this.loop.tick(Date.now());
    return this.applyPrestigeAction(buyPrestigeUpgrade(this.loop.snapshot(), upgrade));
  }

  assignPassiveSkills(skills: SkillId[]): PrestigeActionResult {
    this.loop.tick(Date.now());
    return this.applyPrestigeAction(setPassiveSkills(this.loop.snapshot(), skills));
  }

  selectDoctrine(doctrine: DoctrineId, focusedSkill?: SkillId): DoctrineActionResult {
    this.loop.tick(Date.now());
    const result = chooseDoctrine(this.loop.snapshot(), doctrine, focusedSkill);
    if (result.ok) {
      this.loop.replaceState(result.state);
      this.emit(result.state);
      void this.autosave.flush();
    }
    return result;
  }

  claimContract(contractId: string): ActivityActionResult {
    this.loop.tick(Date.now());
    return this.applyActivityAction(claimContractReward(this.loop.snapshot(), contractId));
  }

  useStoredReward(rewardId: string): ActivityActionResult {
    this.loop.tick(Date.now());
    return this.applyActivityAction(useReward(this.loop.snapshot(), rewardId));
  }

  openRift(): ActivityActionResult {
    this.loop.tick(Date.now());
    return this.applyActivityAction(openRealmRift(this.loop.snapshot()));
  }

  buyConstellationNode(node: ConstellationNodeId): PrestigeActionResult {
    this.loop.tick(Date.now());
    return this.applyPrestigeAction(buyConstellationNode(this.loop.snapshot(), node));
  }

  craftArtifact(artifactId: string): ActivityActionResult {
    this.loop.tick(Date.now());
    return this.applyActivityAction(craftArtifact(this.loop.snapshot(), artifactId));
  }

  async flush(): Promise<void> { await this.autosave.flush(); }

  async replaceFromCloud(state: GameState): Promise<void> {
    this.loop.replaceState(state);
    this.emit(state);
    await this.autosave.flush();
  }

  async linkAccount(userId: string): Promise<void> {
    const state = this.loop.snapshot();
    state.playerId = userId;
    this.loop.replaceState(state);
    this.emit(state);
    await this.autosave.flush();
  }

  destroy(): void {
    this.loop.stop();
    this.autosave.stop();
    this.unsubscribeLoop?.();
    this.unsubscribeLoop = null;
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.listeners.clear();
  }

  private emit(state: GameState): void {
    for (const listener of this.listeners) listener(state);
  }

  private applyEconomyAction(result: EconomyActionResult): EconomyActionResult {
    if (result.ok) {
      this.loop.replaceState(result.state);
      this.emit(result.state);
      void this.autosave.flush();
    }
    return result;
  }

  private applyPrestigeAction(result: PrestigeActionResult): PrestigeActionResult {
    if (result.ok) {
      this.loop.replaceState(result.state);
      this.emit(result.state);
      void this.autosave.flush();
    }
    return result;
  }

  private applyActivityAction(result: ActivityActionResult): ActivityActionResult {
    if (result.ok) {
      this.loop.replaceState(result.state);
      this.emit(result.state);
      void this.autosave.flush();
    }
    return result;
  }
}
